import { useState } from "react";
import { motion } from "framer-motion";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { StartupGrid } from "@/components/startup/StartupGrid";
import { StartupHeader } from "@/components/startup/StartupHeader";
import { StartupSearch } from "@/components/startup/StartupSearch";
import { useDebounce } from "@/hooks/useDebounce";

const ADMIN_ID = 'efc01163-0b41-48c8-8bda-12ea1e994833';

const Startups = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.id === ADMIN_ID;
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 1000); // Changed from 2000 to 1000 (1 second)
  const [filters, setFilters] = useState({
    stage: [],
    continent: [],
    teamSize: [],
    isPro: null,
    category: [],
  });

  const { data: startups, isLoading } = useQuery({
    queryKey: ['startups', debouncedSearch, filters],
    queryFn: async () => {
      let query = supabase
        .from('startups')
        .select(`
          *,
          profiles(is_pro),
          pinned_startups(id)
        `);

      if (debouncedSearch) {
        query = query.or(`name.ilike.%${debouncedSearch}%,description.ilike.%${debouncedSearch}%`);
      }

      if (filters.stage.length > 0) {
        query = query.in('stage', filters.stage);
      }

      if (filters.continent.length > 0) {
        const continentFilter = filters.continent.map(c => `location.ilike.%${c}%`).join(',');
        query = query.or(continentFilter);
      }

      if (filters.teamSize.length > 0) {
        const teamSizeConditions = filters.teamSize.map(size => {
          const [min, max] = size.split('-').map(Number);
          if (max) {
            return `team_size.gte.${min},team_size.lte.${max}`;
          }
          return `team_size.gte.${min}`;
        }).join(',');
        query = query.or(teamSizeConditions);
      }

      if (filters.isPro !== null) {
        query = query.eq('is_pro', filters.isPro);
      }

      if (filters.category.length > 0) {
        query = query.in('category', filters.category);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching startups:', error);
        return [];
      }

      return data || [];
    }
  });

  const pinStartupMutation = useMutation({
    mutationFn: async (startupId: string) => {
      const { data, error } = await supabase
        .from('pinned_startups')
        .insert([{ startup_id: startupId, pinned_by: user?.id }]);
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['startups'] });
      toast({
        title: "Success",
        description: "Startup pinned successfully",
      });
    },
    onError: (error) => {
      console.error('Error pinning startup:', error);
      toast({
        title: "Error",
        description: "Failed to pin startup",
        variant: "destructive",
      });
    },
  });

  const unpinStartupMutation = useMutation({
    mutationFn: async (startupId: string) => {
      const { data, error } = await supabase
        .from('pinned_startups')
        .delete()
        .eq('startup_id', startupId);
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['startups'] });
      toast({
        title: "Success",
        description: "Startup unpinned successfully",
      });
    },
    onError: (error) => {
      console.error('Error unpinning startup:', error);
      toast({
        title: "Error",
        description: "Failed to unpin startup",
        variant: "destructive",
      });
    },
  });

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const pinnedStartups = startups?.filter(startup => startup.pinned_startups?.length > 0) || [];
  const unpinnedStartups = startups?.filter(startup => !startup.pinned_startups?.length) || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="container mx-auto py-8 px-4"
    >
      <StartupHeader />
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="bg-flux-black/50 border-flux-cyan/20 animate-pulse">
              <CardHeader className="h-48 bg-gray-700/20" />
              <CardContent className="space-y-4">
                <div className="h-6 bg-gray-700/20 rounded" />
                <div className="h-16 bg-gray-700/20 rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          {pinnedStartups.length > 0 && (
            <div className="mb-12">
              <StartupGrid
                startups={pinnedStartups}
                isAdmin={isAdmin}
                onPin={(id) => pinStartupMutation.mutate(id)}
                onUnpin={(id) => unpinStartupMutation.mutate(id)}
                title="Featured Startups"
              />
            </div>
          )}
          
          <StartupSearch
            search={searchInput}
            onSearchChange={setSearchInput}
            filters={filters}
            onFilterChange={handleFilterChange}
          />

          <StartupGrid
            startups={unpinnedStartups}
            isAdmin={isAdmin}
            onPin={(id) => pinStartupMutation.mutate(id)}
            onUnpin={(id) => unpinStartupMutation.mutate(id)}
          />
        </>
      )}
    </motion.div>
  );
};

export default Startups;
